import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Sparkles, Target, Archive, RefreshCw, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const asWhy = (v) => (Array.isArray(v) ? v.filter(Boolean) : v ? [v] : []);

export const AIDiscoveryDialog = ({
  open, onOpenChange, title = "AI Discovery Search", subtitle,
  searchEndpoint, archiveEndpoint, searchBody, getKey, renderCandidate, getWhy, onArchived, renderSummary,
}) => {
  const [phase, setPhase] = useState("idle"); // idle | searching | list | archiving
  const [candidates, setCandidates] = useState([]);
  const [sel, setSel] = useState({});
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);

  // Alcuni endpoint (Rivenditori) rispondono con {job_id}: la pipeline gira in background → polling.
  const pollJob = (jobId) => new Promise((resolve, reject) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/discover-jobs/${jobId}`);
        const j = data.job;
        if (j.progress) setProgress(j.progress);
        if (j.status === "ready") { clearInterval(iv); resolve(j.result || {}); }
        else if (j.status === "error") { clearInterval(iv); reject(new Error(j.error || "Ricerca fallita")); }
        else if (tries > 420) { clearInterval(iv); reject(new Error("La ricerca sta impiegando troppo tempo. Riprova.")); }
      } catch (e) { if (tries > 420) { clearInterval(iv); reject(e); } }
    }, 2500);
  });

  const runSearch = useCallback(async () => {
    setPhase("searching");
    setCandidates([]);
    setSel({});
    setProgress(null);
    setSummary(null);
    try {
      let { data } = await api.post(searchEndpoint, searchBody);
      if (data?.job_id) data = await pollJob(data.job_id);
      const list = data.candidates || [];
      setCandidates(list);
      setSummary(data);
      // preselezione: solo i candidati non "da verificare" (quelli sotto soglia restano deselezionati)
      const s = {};
      list.forEach((c, i) => { s[getKey(c, i)] = c.qualification !== "da_verificare"; });
      setSel(s);
      setPhase("list");
      if (!list.length) toast("Nessun candidato trovato · modifica i filtri e riprova");
    } catch (e) {
      setPhase("list");
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [searchEndpoint, searchBody, getKey]);

  useEffect(() => {
    if (open) runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedItems = candidates.filter((c, i) => sel[getKey(c, i)]);
  const allSelected = candidates.length > 0 && selectedItems.length === candidates.length;

  const toggleAll = () => {
    if (allSelected) { setSel({}); return; }
    const s = {};
    candidates.forEach((c, i) => { s[getKey(c, i)] = true; });
    setSel(s);
  };

  const archive = async () => {
    if (!selectedItems.length) { toast.error("Seleziona almeno un candidato"); return; }
    setPhase("archiving");
    try {
      const { data } = await api.post(archiveEndpoint, { items: selectedItems });
      toast.success(`${data.saved || selectedItems.length} salvati in archivio`);
      onArchived?.(data);
      onOpenChange(false);
    } catch (e) {
      setPhase("list");
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0" data-testid="ai-discovery-dialog">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" /> {title}
          </DialogTitle>
          <DialogDescription>{subtitle || "Ricerca iper-veloce: seleziona i candidati e archiviali. I contatti vengono recuperati solo per i selezionati."}</DialogDescription>
        </DialogHeader>

        {phase === "searching" && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center" data-testid="ai-discovery-loading">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">{progress?.engine === "brema-qualifier" ? "Discovery e qualificazione in corso…" : "Ricerca iper-veloce in corso…"}</p>
            {progress?.engine === "brema-qualifier" ? (
              <p className="text-xs text-muted-foreground" data-testid="ai-discovery-progress">
                {progress.phase === "discovery" && `Discovery ampia · query ${progress.queries_done ?? 0}/${progress.queries_total ?? "?"} · ${progress.candidates ?? 0} candidati`}
                {progress.phase === "analyzing" && `Analisi siti e qualificazione · ${progress.analyzed ?? 0}/${progress.max_analyzed ?? 50} analizzati`}
                {(progress.phase === "qualifying" || progress.phase === "done") && `Qualificati ${progress.qualified ?? 0}/${progress.target ?? "?"} · ${progress.to_verify ?? 0} da verificare · ${progress.excluded ?? 0} esclusi`}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">L'AI sta individuando i candidati in target</p>
            )}
          </div>
        )}

        {(phase === "list" || phase === "archiving") && (
          <>
            {renderSummary && summary && (
              <div className="border-b px-5 py-2.5" data-testid="ai-discovery-summary">{renderSummary(summary)}</div>
            )}
            <div className="flex items-center justify-between px-5 py-2.5 text-sm">
              <label className="flex cursor-pointer items-center gap-2 font-medium">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="ai-discovery-select-all" />
                {candidates.length ? `${selectedItems.length}/${candidates.length} selezionati` : "Nessun risultato"}
              </label>
              <Button variant="ghost" size="sm" onClick={runSearch} disabled={phase === "archiving"} data-testid="ai-discovery-rerun" className="gap-1.5 text-xs">
                <RefreshCw className="h-3.5 w-3.5" /> Nuova ricerca
              </Button>
            </div>

            <ScrollArea className="max-h-[52vh] px-5">
              <div className="space-y-2.5 pb-2">
                {candidates.length === 0 && phase === "list" && (
                  <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
                    <Search className="h-7 w-7" />
                    <p className="text-sm">Nessun candidato. Modifica i filtri e riprova.</p>
                  </div>
                )}
                {candidates.map((c, i) => {
                  const k = getKey(c, i);
                  const why = asWhy(getWhy?.(c));
                  const checked = !!sel[k];
                  return (
                    <div key={k} data-testid={`ai-discovery-cand-${i}`}
                      className={`flex gap-3 rounded-xl border p-3 transition-colors ${checked ? "border-primary/50 bg-primary/[0.04]" : "border-border"}`}>
                      <Checkbox className="mt-0.5" checked={checked}
                        onCheckedChange={(v) => setSel((p) => ({ ...p, [k]: !!v }))}
                        data-testid={`ai-discovery-check-${i}`} />
                      <div className="min-w-0 flex-1">
                        {renderCandidate(c)}
                        {why.length > 0 && (
                          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 dark:border-amber-900/40 dark:bg-amber-950/20">
                            <div className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-500">
                              <Target className="h-3 w-3" /> Perché è in target
                            </div>
                            <ul className="space-y-0.5 text-xs text-amber-900/90 dark:text-amber-200/90">
                              {why.slice(0, 3).map((w, j) => <li key={j}>{why.length > 1 ? "• " : ""}{w}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={phase === "archiving"}>Annulla</Button>
              <Button onClick={archive} disabled={phase === "archiving" || !selectedItems.length}
                className="gap-1.5 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
                data-testid="ai-discovery-archive-btn">
                {phase === "archiving" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                Archivia ({selectedItems.length})
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIDiscoveryDialog;
